import { forwardRef, type ReactNode } from 'react';
import { Checkbox as FluentCheckbox, mergeClasses } from '@fluentui/react-components';
import type { CheckboxProps as FluentCheckboxProps } from '@fluentui/react-components';
import { useCheckboxStyles } from './Checkbox.styles';

export interface CheckboxProps extends Omit<FluentCheckboxProps, 'label'> {
  /** Label text or React node (supports embedded links, formatted text) */
  label?: string | ReactNode;
  /** Shows red asterisk (#B10E1C) next to label when true */
  required?: boolean;
  /** Wraps checkbox in a highlighted container */
  highlighted?: boolean;
  /** Optional CSS class merged with component styles */
  className?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, required, highlighted, className, ...rest }, ref) => {
    const styles = useCheckboxStyles();

    // Build the label with optional required asterisk
    let renderedLabel: ReactNode = label;
    if (label != null && required) {
      renderedLabel = (
        <>
          {label}
          <span className={styles.requiredAsterisk}> *</span>
        </>
      );
    }

    const checkbox = (
      <FluentCheckbox
        ref={ref}
        className={mergeClasses(styles.root, className)}
        label={renderedLabel as string}
        {...rest}
      />
    );

    if (highlighted) {
      return (
        <div data-testid="checkbox-highlight" className={styles.highlightedContainer}>
          {checkbox}
        </div>
      );
    }

    return checkbox;
  }
);

Checkbox.displayName = 'Checkbox';
